export const LIVE_RELAY_DEFAULT_PORT = 43125;
export const LIVE_RELAY_DEFAULT_HOST = '127.0.0.1';
export const LIVE_RELAY_DEFAULT_HTTP_ORIGIN = `http://${LIVE_RELAY_DEFAULT_HOST}:${LIVE_RELAY_DEFAULT_PORT}`;
export const LIVE_RELAY_DEFAULT_WS_URL = `ws://${LIVE_RELAY_DEFAULT_HOST}:${LIVE_RELAY_DEFAULT_PORT}/ws`;
export const LIVE_RELAY_ENV_ORIGIN = process.env.REACT_APP_LIVE_RELAY_ORIGIN || '';
export const LIVE_LAUNCH_MODE_LOCAL = 'local';
export const LIVE_LAUNCH_MODE_ONLINE = 'online';

export const LIVE_PHASE_IDLE = 'idle';
export const LIVE_PHASE_PLAYING = 'playing';
export const LIVE_PHASE_PAUSED = 'paused';

export const RELAY_ERROR_CODES = {
  ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
  ROOM_EXPIRED: 'ROOM_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_HELLO: 'INVALID_HELLO',
  ROOM_LIMIT_REACHED: 'ROOM_LIMIT_REACHED',
  FORBIDDEN_ORIGIN: 'FORBIDDEN_ORIGIN',
  RATE_LIMITED: 'RATE_LIMITED',
  DISCONNECTED: 'DISCONNECTED',
};

export const createEmptyLiveSessionState = () => ({
  revision: 1,
  updatedAt: Date.now(),
  playlistItems: [],
  previewItemId: null,
  program: null,
  phase: LIVE_PHASE_IDLE,
  playbackStartedAt: null,
  playbackElapsedMs: 0,
});

const getDefaultLocationLike = () => (
  typeof window === 'undefined' ? null : window.location
);

const getLocationBase = (locationLike = getDefaultLocationLike()) => {
  if (!locationLike) {
    return {
      origin: '',
      pathname: '/',
    };
  }
  return {
    origin: locationLike.origin || '',
    pathname: locationLike.pathname || '/',
  };
};

const safeParseUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    return new URL(value.trim());
  } catch {
    return null;
  }
};

const normalizeRelayHttpOrigin = (value) => {
  const parsed = safeParseUrl(value);
  if (!parsed) return '';
  return (parsed.protocol === 'http:' || parsed.protocol === 'https:') ? parsed.origin : '';
};

const withModeUrl = (mode, locationLike = getDefaultLocationLike()) => {
  const { origin, pathname } = getLocationBase(locationLike);
  const base = origin ? `${origin}${pathname}` : '/';
  const url = new URL(base, origin || 'http://localhost');
  url.searchParams.set('mode', mode);
  return url;
};

const buildWsUrlFromHttpOrigin = (httpOrigin) => {
  const parsed = safeParseUrl(httpOrigin);
  if (!parsed) return LIVE_RELAY_DEFAULT_WS_URL;
  const wsUrl = new URL(parsed.origin);
  wsUrl.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
  wsUrl.pathname = '/ws';
  wsUrl.search = '';
  wsUrl.hash = '';
  return wsUrl.toString();
};

const buildRelayConfigFromOverride = (override) => {
  const parsed = safeParseUrl(override);
  if (!parsed) return null;

  if (parsed.protocol === 'ws:' || parsed.protocol === 'wss:') {
    const httpOrigin = `${parsed.protocol === 'wss:' ? 'https:' : 'http:'}//${parsed.host}`;
    const wsUrl = new URL(parsed.toString());
    if (!wsUrl.pathname || wsUrl.pathname === '/') wsUrl.pathname = '/ws';
    return { httpOrigin, wsUrl: wsUrl.toString() };
  }

  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    const wsUrl = parsed.pathname === '/ws'
      ? (() => {
        const next = new URL(parsed.toString());
        next.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
        return next.toString();
      })()
      : buildWsUrlFromHttpOrigin(parsed.origin);
    return { httpOrigin: parsed.origin, wsUrl };
  }

  return null;
};

export const resolveRelayConfig = (
  search = '',
  envOrigin = LIVE_RELAY_ENV_ORIGIN,
) => {
  const override = getRelayOverrideFromSearch(search);
  const overrideConfig = buildRelayConfigFromOverride(override);
  if (overrideConfig) return overrideConfig;

  const envConfig = buildRelayConfigFromOverride(envOrigin);
  if (envConfig) return envConfig;

  return {
    httpOrigin: LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
    wsUrl: LIVE_RELAY_DEFAULT_WS_URL,
  };
};

export const getRelayUrlFromSearch = (search = '') => (
  resolveRelayConfig(search).wsUrl
);

export const getRelayHttpOriginFromSearch = (search = '') => (
  resolveRelayConfig(search).httpOrigin
);

export const getRelayOverrideFromSearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  return normalizeRelayHttpOrigin(params.get('relay'));
};

export const getCreateRoomApiUrlFromOrigin = (httpOrigin = '') => (
  new URL('/api/rooms', normalizeRelayHttpOrigin(httpOrigin) || LIVE_RELAY_DEFAULT_HTTP_ORIGIN).toString()
);

export const getCreateRoomApiUrl = (search = '') => (
  getCreateRoomApiUrlFromOrigin(getRelayHttpOriginFromSearch(search))
);

export const getRoomIdFromSearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  const roomId = params.get('room');
  return typeof roomId === 'string' && roomId.trim() ? roomId.trim() : '';
};

export const getControlTokenFromHash = (hash = '') => {
  const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  const token = params.get('token');
  return typeof token === 'string' && token.trim() ? token.trim() : '';
};

export const buildEditorUrl = (locationLike = getDefaultLocationLike()) => (
  withModeUrl('editor', locationLike).toString()
);

export const buildLiveControlUrl = (
  { roomId = '', controlToken = '', relayOrigin = '' } = {},
  locationLike = getDefaultLocationLike(),
) => {
  const url = withModeUrl('live', locationLike);
  if (roomId) url.searchParams.set('room', roomId);
  const normalizedRelayOrigin = normalizeRelayHttpOrigin(relayOrigin);
  if (normalizedRelayOrigin) url.searchParams.set('relay', normalizedRelayOrigin);
  if (controlToken) url.hash = `token=${encodeURIComponent(controlToken)}`;
  return url.toString();
};

export const buildOutputUrl = (
  { roomId = '', relayOrigin = '' } = {},
  locationLike = getDefaultLocationLike(),
) => {
  const url = withModeUrl('output', locationLike);
  if (roomId) url.searchParams.set('room', roomId);
  const normalizedRelayOrigin = normalizeRelayHttpOrigin(relayOrigin);
  if (normalizedRelayOrigin) url.searchParams.set('relay', normalizedRelayOrigin);
  return url.toString();
};

export const getRelayClockNow = (serverClockOffsetMs = 0, now = Date.now()) => (
  now + (Number.isFinite(serverClockOffsetMs) ? serverClockOffsetMs : 0)
);
